import type { PointPt, RectPt, ShapeTarget } from "@/lib/protocol";
import type { PageExtent, TextLayerData } from "@/lib/text-layer";

export const MARK_STROKE_PT = 2.75;
export const ARROW_HEAD_LENGTH_PER_WIDTH = 24 / 4.2;
export const ARROW_HEAD_HALF_WIDTH_PER_WIDTH = 9.5 / 4.2;
export const PIP_RADIUS_PT = 9.8;
export const PIP_RING_PT = 1.7;
export const MAX_SHAPE_STROKE_PT = 5;
export const PIP_CLEARANCE_PT = PIP_RADIUS_PT + PIP_RING_PT / 2 + MAX_SHAPE_STROKE_PT / 2;
export const PIP_OFFSET_PT = 10;
export const PIP_PAD_PT = 11.8;
export const PIP_FONT_PT = 8.5;

export function ptToMm(pt: number): number {
  return pt * 25.4 / 72;
}

function splitsPair(text: string, offset: number): boolean {
  return offset > 0 && offset < text.length
    && /[\uD800-\uDBFF]/.test(text[offset - 1] ?? "")
    && /[\uDC00-\uDFFF]/.test(text[offset] ?? "");
}

export function snapRangeToCodePoints(text: string, start: number, end: number): { start: number; end: number } {
  return { start: splitsPair(text, start) ? start - 1 : start, end: splitsPair(text, end) ? end + 1 : end };
}

export function quadsForRange(layer: TextLayerData, start: number, end: number): RectPt[] {
  const quads: RectPt[] = [];
  const lineBounds = layer.lines.map(([first, lastExclusive]) => {
    const words = layer.words.slice(first, lastExclusive);
    return words.length ? { top: Math.min(...words.map(([, y1]) => y1)), bottom: Math.max(...words.map(([, , , y2]) => y2)) } : null;
  });
  for (const [lineIndex, [first, lastExclusive]] of layer.lines.entries()) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (let index = first; index < lastExclusive; index += 1) {
      const word = layer.words[index];
      if (!word) continue;
      const [xMin, yMin, xMax, yMax, wordStart, wordEnd] = word;
      if (wordStart >= end || wordEnd <= start) continue;
      const span = wordEnd - wordStart;
      const x1 = span === 0 ? xMin : xMin + (xMax - xMin) * (Math.max(start, wordStart) - wordStart) / span;
      const x2 = span === 0 ? xMax : xMin + (xMax - xMin) * (Math.min(end, wordEnd) - wordStart) / span;
      minX = Math.min(minX, x1);
      minY = Math.min(minY, yMin);
      maxX = Math.max(maxX, x2);
      maxY = Math.max(maxY, yMax);
    }
    if (Number.isFinite(minX)) {
      const centre = (minY + maxY) / 2;
      const previous = lineBounds[lineIndex - 1];
      const next = lineBounds[lineIndex + 1];
      const previousCentre = previous ? (previous.top + previous.bottom) / 2 : null;
      const nextCentre = next ? (next.top + next.bottom) / 2 : null;
      const pitch = previousCentre !== null && nextCentre !== null
        ? (nextCentre - previousCentre) / 2
        : previousCentre !== null ? centre - previousCentre
          : nextCentre !== null ? nextCentre - centre
            : maxY - minY;
      // A line far from its neighbours (a headline over body text) keeps a bar near its own
      // type size instead of stretching across the empty space to the next line.
      const height = Math.max(maxY - minY, Math.min(pitch, (maxY - minY) * 1.5));
      quads.push([minX, centre - height / 2, maxX, centre + height / 2]);
    }
  }
  return quads;
}

export function arrowHead(tail: PointPt, head: PointPt, widthPt: number): { shaft: [PointPt, PointPt]; head: [PointPt, PointPt, PointPt] } {
  const dx = head[0] - tail[0];
  const dy = head[1] - tail[1];
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const hl = widthPt * ARROW_HEAD_LENGTH_PER_WIDTH;
  const hw = widthPt * ARROW_HEAD_HALF_WIDTH_PER_WIDTH;
  const base: PointPt = [head[0] - ux * hl, head[1] - uy * hl];
  return {
    shaft: [tail, base],
    head: [head, [base[0] - uy * hw, base[1] + ux * hw], [base[0] + uy * hw, base[1] - ux * hw]],
  };
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  if (words.length === 0) return "";
  const first = Array.from(words[0] ?? "")[0] ?? "";
  const last = Array.from(words[words.length - 1] ?? "")[0] ?? "";
  return `${first}${words.length === 1 ? "" : last}`.toUpperCase();
}

export function pipCentre(target: ShapeTarget, kind: "region" | "rect" | "ellipse" | "line" | "arrow" | "ink", extent: PageExtent): PointPt {
  const [x1, y1, x2, y2] = target.rect;
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const centre: PointPt = [(left + Math.max(x1, x2)) / 2, (top + Math.max(y1, y2)) / 2];
  const anchor: PointPt = kind === "region" || kind === "rect" || kind === "ellipse"
    ? target.origin ?? [left, top]
    : kind === "line" || kind === "arrow"
      ? target.points?.[0] ?? [0, 0]
      : target.path?.length
        ? [Math.min(...target.path.map((point) => point[0])), Math.min(...target.path.map((point) => point[1]))]
        : [left, top];
  const raw: PointPt = [
    anchor[0] + (anchor[0] <= centre[0] ? -PIP_OFFSET_PT : PIP_OFFSET_PT),
    anchor[1] + (anchor[1] <= centre[1] ? -PIP_OFFSET_PT : PIP_OFFSET_PT),
  ];
  return [
    Math.max(PIP_PAD_PT, Math.min(extent.widthPt - PIP_PAD_PT, raw[0])),
    Math.max(PIP_PAD_PT, Math.min(extent.heightPt - PIP_PAD_PT, raw[1])),
  ];
}
